import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Sparkles, Search, ShieldCheck, PauseCircle, PlayCircle, CalendarClock, Upload,
  BookOpen, Wand2, CheckCircle2, Clock, BarChart3, Cpu, Loader2, RefreshCw, Users
} from 'lucide-react';
import api from '../../lib/api';

const STATE_BADGE = {
  ativo: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  nao_liberado: 'bg-slate-100 text-slate-600 border-slate-200',
  suspenso: 'bg-amber-100 text-amber-700 border-amber-200',
  expirado: 'bg-red-100 text-red-700 border-red-200',
  agendado: 'bg-sky-100 text-sky-700 border-sky-200',
};
const STATE_LABEL = {
  ativo: 'Ativo', nao_liberado: 'Não liberado', suspenso: 'Suspenso',
  expirado: 'Expirado', agendado: 'Agendado',
};
const TYPE_LABEL = { courtesy: 'Cortesia', institutional: 'Institucional', contracted: 'Contratado', pilot: 'Piloto' };
const STATUS_LABEL = {
  rascunho: 'Rascunho', em_revisao: 'Em revisão', aprovado: 'Aprovado',
  publicado: 'Publicado', arquivado: 'Arquivado',
};

const Kpi = ({ label, value, tone = 'violet', testid }) => (
  <Card className="border-slate-200" data-testid={testid}>
    <CardContent className="p-4">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-2xl font-bold text-${tone}-700`}>{value}</p>
    </CardContent>
  </Card>
);

export default function DnaEstudosAdmin() {
  const [tab, setTab] = useState('liberacao');
  const [overview, setOverview] = useState(null);

  // liberação
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantForm, setGrantForm] = useState({ access_type: 'courtesy', starts_at: '', ends_at: '', reason: '' });
  const [saving, setSaving] = useState(false);

  // bncc
  const [skills, setSkills] = useState([]);
  const [skillsTotal, setSkillsTotal] = useState(0);
  const [skillSearch, setSkillSearch] = useState('');
  const [imports, setImports] = useState([]);
  const [template, setTemplate] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // estúdio
  const [contents, setContents] = useState([]);
  const [counts, setCounts] = useState({});
  const [contentStatus, setContentStatus] = useState('all');
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [detail, setDetail] = useState(null);

  // painel + IA
  const [painel, setPainel] = useState(null);
  const [usage, setUsage] = useState(null);
  const [settings, setSettings] = useState(null);

  const loadOverview = useCallback(async () => {
    try {
      const { data } = await api.get('/dna-estudos/admin/overview');
      setOverview(data);
    } catch { /* silencioso */ }
  }, []);

  const loadStudents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: '15' });
      if (search) params.set('search', search);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const { data } = await api.get(`/dna-estudos/admin/students?${params}`);
      setRows(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar alunos');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  const loadSkills = useCallback(async () => {
    const params = new URLSearchParams({ page: '1', page_size: '30' });
    if (skillSearch) params.set('search', skillSearch);
    const { data } = await api.get(`/dna-estudos/bncc/skills?${params}`);
    setSkills(data.items || []);
    setSkillsTotal(data.total || 0);
  }, [skillSearch]);

  const loadContents = useCallback(async () => {
    const params = new URLSearchParams({ page: '1', page_size: '20' });
    if (contentStatus !== 'all') params.set('status', contentStatus);
    const { data } = await api.get(`/dna-estudos/studio/contents?${params}`);
    setContents(data.items || []);
    setCounts(data.counts || {});
  }, [contentStatus]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { if (tab === 'liberacao') loadStudents(); }, [tab, loadStudents]);
  useEffect(() => {
    if (tab === 'bncc') {
      loadSkills();
      api.get('/dna-estudos/bncc/imports').then(r => setImports(r.data.items || [])).catch(() => {});
      api.get('/dna-estudos/bncc/template').then(r => setTemplate(r.data)).catch(() => {});
    }
  }, [tab, loadSkills]);
  useEffect(() => {
    if (tab === 'estudio') { loadContents(); loadSkills(); }
  }, [tab, loadContents, loadSkills]);
  useEffect(() => {
    if (tab === 'painel') api.get('/dna-estudos/painel/turmas').then(r => setPainel(r.data)).catch(() => {});
    if (tab === 'ia') {
      api.get('/dna-estudos/admin/ai-usage').then(r => setUsage(r.data)).catch(() => {});
      api.get('/dna-estudos/admin/settings').then(r => setSettings(r.data)).catch(() => {});
    }
  }, [tab]);

  const toggleAll = () => setSelected(selected.length === rows.length ? [] : rows.map(r => r.student_id));
  const toggleOne = (id) => setSelected(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));

  const runAction = async (path, payload, successMsg) => {
    setSaving(true);
    try {
      await api.post(`/dna-estudos/admin/access/${path}`, payload);
      toast.success(successMsg);
      setSelected([]);
      setGrantOpen(false);
      await Promise.all([loadStudents(), loadOverview()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não foi possível concluir a ação');
    } finally {
      setSaving(false);
    }
  };

  const handleImport = async (file, dryRun) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post(`/dna-estudos/bncc/import?dry_run=${dryRun}`, form,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(data);
      toast.success(dryRun ? 'Validação concluída (nada foi gravado)' : `Importação concluída · ${data.inserted} novas, ${data.updated} atualizadas`);
      if (!dryRun) { loadSkills(); api.get('/dna-estudos/bncc/imports').then(r => setImports(r.data.items || [])); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha na importação');
    } finally {
      setImporting(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedSkills.length) return toast.error('Selecione ao menos uma habilidade');
    setGenerating(true);
    try {
      const { data } = await api.post('/dna-estudos/studio/generate', { skill_codes: selectedSkills });
      toast.success(`${data.created.length} conteúdo(s) gerado(s) como rascunho`);
      if (data.failed?.length) toast.error(`${data.failed.length} falharam: ${data.failed[0].erro}`);
      setSelectedSkills([]);
      loadContents();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao gerar conteúdo');
    } finally {
      setGenerating(false);
    }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.post(`/dna-estudos/studio/contents/${id}/status`, { status });
      toast.success(`Conteúdo em "${STATUS_LABEL[status]}"`);
      loadContents();
      if (detail?.id === id) setDetail({ ...detail, status });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não foi possível alterar o status');
    }
  };

  const saveSettings = async () => {
    try {
      const { data } = await api.put('/dna-estudos/admin/settings', settings);
      setSettings(data);
      toast.success('Configurações salvas');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar');
    }
  };

  return (
    <div className="p-3 sm:p-6 space-y-5" data-testid="dna-estudos-admin">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-violet-700" />
            DNA IA Estudos
          </h1>
          <p className="text-sm text-muted-foreground">Reforço inteligente por habilidade da BNCC · liberação, conteúdo e acompanhamento</p>
        </div>
        <Button variant="outline" onClick={() => { loadOverview(); loadStudents(); }} data-testid="estudos-refresh">
          <RefreshCw className="w-4 h-4 mr-2" />Atualizar
        </Button>
      </div>

      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <Kpi label="Alunos ativos" value={overview.total_students} testid="kpi-total" />
          <Kpi label="Acessos ativos" value={overview.active} tone="emerald" testid="kpi-ativos" />
          <Kpi label="Não liberados" value={overview.not_released} tone="slate" testid="kpi-nao-liberados" />
          <Kpi label="Suspensos" value={overview.suspended} tone="amber" testid="kpi-suspensos" />
          <Kpi label="Expirando (15d)" value={overview.expiring_15d} tone="red" testid="kpi-expirando" />
          <Kpi label="Usaram (7 dias)" value={overview.used_last_7d} testid="kpi-uso" />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="liberacao" data-testid="tab-liberacao"><ShieldCheck className="w-4 h-4 mr-2" />Liberação</TabsTrigger>
          <TabsTrigger value="bncc" data-testid="tab-bncc"><BookOpen className="w-4 h-4 mr-2" />BNCC</TabsTrigger>
          <TabsTrigger value="estudio" data-testid="tab-estudio"><Wand2 className="w-4 h-4 mr-2" />Estúdio</TabsTrigger>
          <TabsTrigger value="painel" data-testid="tab-painel"><BarChart3 className="w-4 h-4 mr-2" />Painel</TabsTrigger>
          <TabsTrigger value="ia" data-testid="tab-ia"><Cpu className="w-4 h-4 mr-2" />IA e Custos</TabsTrigger>
        </TabsList>

        {/* ── LIBERAÇÃO ─────────────────────────────── */}
        <TabsContent value="liberacao" className="space-y-3">
          <Card>
            <CardContent className="p-3 sm:p-4 space-y-3">
              <div className="flex flex-col md:flex-row gap-2">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
                  <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                         placeholder="Buscar por nome ou matrícula" className="pl-9"
                         data-testid="estudos-busca" />
                </div>
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-full md:w-52" data-testid="estudos-filtro-status">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os status</SelectItem>
                    {Object.entries(STATE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {selected.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg bg-violet-50 border border-violet-200 p-2">
                  <span className="text-sm font-medium text-violet-900">{selected.length} selecionado(s)</span>
                  <Button size="sm" onClick={() => setGrantOpen(true)} data-testid="estudos-liberar-lote">
                    <ShieldCheck className="w-4 h-4 mr-1" />Liberar / renovar
                  </Button>
                  <Button size="sm" variant="outline" disabled={saving}
                          onClick={() => runAction('suspend', { student_ids: selected, reason: 'Suspensão administrativa' }, 'Acesso suspenso')}
                          data-testid="estudos-suspender">
                    <PauseCircle className="w-4 h-4 mr-1" />Suspender
                  </Button>
                  <Button size="sm" variant="outline" disabled={saving}
                          onClick={() => runAction('reactivate', { student_ids: selected }, 'Acesso reativado')}
                          data-testid="estudos-reativar">
                    <PlayCircle className="w-4 h-4 mr-1" />Reativar
                  </Button>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="p-2 text-left"><input type="checkbox" checked={rows.length > 0 && selected.length === rows.length}
                                                            onChange={toggleAll} data-testid="estudos-check-all" /></th>
                      <th className="p-2 text-left">Aluno</th>
                      <th className="p-2 text-left">Matrícula</th>
                      <th className="p-2 text-left">Turma</th>
                      <th className="p-2 text-left">Status</th>
                      <th className="p-2 text-left">Modalidade</th>
                      <th className="p-2 text-left">Validade</th>
                      <th className="p-2 text-left">Último acesso</th>
                      <th className="p-2 text-right">Progresso</th>
                      <th className="p-2 text-right">Tempo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && <tr><td colSpan={10} className="p-6 text-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Carregando…</td></tr>}
                    {!loading && rows.length === 0 && <tr><td colSpan={10} className="p-6 text-center text-muted-foreground">Nenhum aluno encontrado</td></tr>}
                    {rows.map((r) => (
                      <tr key={r.student_id} className="border-b hover:bg-slate-50" data-testid={`estudos-row-${r.student_id}`}>
                        <td className="p-2"><input type="checkbox" checked={selected.includes(r.student_id)}
                                                   onChange={() => toggleOne(r.student_id)}
                                                   data-testid={`estudos-check-${r.student_id}`} /></td>
                        <td className="p-2 font-medium">{r.name}</td>
                        <td className="p-2 text-muted-foreground">{r.registration_number || '—'}</td>
                        <td className="p-2 text-muted-foreground">{r.class_name || '—'}</td>
                        <td className="p-2"><Badge variant="outline" className={STATE_BADGE[r.state]}>{STATE_LABEL[r.state]}</Badge></td>
                        <td className="p-2 text-muted-foreground">{TYPE_LABEL[r.access_type] || '—'}</td>
                        <td className="p-2 text-muted-foreground">{r.ends_at || '—'}</td>
                        <td className="p-2 text-muted-foreground">{r.last_access ? String(r.last_access).slice(0, 10) : '—'}</td>
                        <td className="p-2 text-right font-semibold text-violet-800">{r.progress}%</td>
                        <td className="p-2 text-right text-muted-foreground">{r.minutes_total} min</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{total} aluno(s)</span>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="estudos-prev">Anterior</Button>
                  <span>{page} / {pages}</span>
                  <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(p => p + 1)} data-testid="estudos-next">Próxima</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── BNCC ─────────────────────────────── */}
        <TabsContent value="bncc" className="space-y-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base md:text-lg">Importar matriz oficial da BNCC</CardTitle>
              <p className="text-xs text-muted-foreground">
                Nenhum código ou texto da BNCC é inventado pelo sistema — a matriz vem da sua planilha oficial.
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              {template && (
                <div className="text-xs text-slate-600 bg-slate-50 border rounded-lg p-3 space-y-1">
                  <p><strong>Obrigatórias:</strong> {template.colunas_obrigatorias.join(', ')}</p>
                  <p><strong>Opcionais:</strong> {template.colunas_opcionais.join(', ')}</p>
                  <p><strong>Formatos:</strong> {template.formatos.join(' · ')}</p>
                  {template.observacoes.map(o => <p key={o}>• {o}</p>)}
                </div>
              )}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                <input type="file" accept=".csv,.xlsx,.xls,.json" id="bncc-file"
                       className="text-sm" data-testid="bncc-file-input" />
                <Button variant="outline" disabled={importing} data-testid="bncc-validar"
                        onClick={() => handleImport(document.getElementById('bncc-file')?.files?.[0], true)}>
                  {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Validar sem gravar
                </Button>
                <Button disabled={importing} data-testid="bncc-importar"
                        onClick={() => handleImport(document.getElementById('bncc-file')?.files?.[0], false)}>
                  <Upload className="w-4 h-4 mr-2" />Importar
                </Button>
              </div>
              {importResult && (
                <div className="text-sm rounded-lg border p-3 bg-white" data-testid="bncc-import-result">
                  <p className="font-medium">Versão {importResult.version} · {importResult.total_rows} linhas</p>
                  <p className="text-muted-foreground">
                    Inseridas {importResult.inserted} · Atualizadas {importResult.updated} · Rejeitadas {importResult.rejected}
                  </p>
                  {importResult.errors?.slice(0, 5).map((e, i) => (
                    <p key={i} className="text-red-600 text-xs">Linha {e.linha}: {e.erro}</p>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base md:text-lg">Catálogo ({skillsTotal})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Input value={skillSearch} onChange={(e) => setSkillSearch(e.target.value)}
                     placeholder="Buscar por código ou texto da habilidade" data-testid="bncc-busca" />
              {skills.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">
                  Catálogo vazio. Importe a matriz da BNCC acima para habilitar o Estúdio de Conteúdo.
                </p>
              ) : (
                <div className="space-y-2">
                  {skills.map(s => (
                    <div key={s.codigo} className="border rounded-lg p-2.5 text-sm" data-testid={`bncc-skill-${s.codigo}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="bg-violet-100 text-violet-800 border-violet-200">{s.codigo}</Badge>
                        <span className="text-xs text-muted-foreground">{s.componente} · {s.ano} · {s.segmento}</span>
                      </div>
                      <p className="mt-1 text-slate-700">{s.habilidade}</p>
                    </div>
                  ))}
                </div>
              )}
              {imports.length > 0 && (
                <div className="pt-2 border-t">
                  <p className="text-xs font-semibold text-slate-600 mb-1">Importações recentes</p>
                  {imports.slice(0, 5).map(i => (
                    <p key={i.id} className="text-xs text-muted-foreground">
                      {String(i.created_at).slice(0, 16).replace('T', ' ')} · {i.version} · +{i.inserted}/~{i.updated}/✕{i.rejected} · {i.imported_by_name}
                    </p>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── ESTÚDIO ─────────────────────────────── */}
        <TabsContent value="estudio" className="space-y-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base md:text-lg">Gerar conteúdo com IA</CardTitle>
              <p className="text-xs text-muted-foreground">
                Máximo de 2 habilidades por lote (cada geração leva ~50s). Todo conteúdo nasce como rascunho e só chega ao aluno após aprovação humana.
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              {skills.length === 0 ? (
                <p className="text-sm text-muted-foreground">Importe a matriz da BNCC para começar a gerar conteúdo.</p>
              ) : (
                <>
                  <div className="max-h-64 overflow-y-auto space-y-1 border rounded-lg p-2">
                    {skills.map(s => (
                      <label key={s.codigo} className="flex items-start gap-2 text-sm p-1.5 rounded hover:bg-slate-50 cursor-pointer">
                        <input type="checkbox" className="mt-1" checked={selectedSkills.includes(s.codigo)}
                               data-testid={`estudio-skill-${s.codigo}`}
                               onChange={() => setSelectedSkills(v => v.includes(s.codigo) ? v.filter(x => x !== s.codigo) : [...v, s.codigo])} />
                        <span><strong className="text-violet-800">{s.codigo}</strong> — {s.habilidade}</span>
                      </label>
                    ))}
                  </div>
                  <Button onClick={handleGenerate} disabled={generating || !selectedSkills.length} data-testid="estudio-gerar">
                    {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                    Gerar ({selectedSkills.length})
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base md:text-lg">Conteúdos</CardTitle>
              <Select value={contentStatus} onValueChange={setContentStatus}>
                <SelectTrigger className="w-44" data-testid="estudio-filtro-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {Object.entries(STATUS_LABEL).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v} ({counts[k] ?? 0})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent className="space-y-2">
              {contents.length === 0 && <p className="text-sm text-muted-foreground py-3">Nenhum conteúdo ainda.</p>}
              {contents.map(c => (
                <div key={c.id} className="border rounded-lg p-3 flex flex-col md:flex-row md:items-center gap-2"
                     data-testid={`estudio-content-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{c.titulo || '(sem título)'}</p>
                    <p className="text-xs text-muted-foreground">
                      {c.skill_code} · {c.skill?.componente} · {c.skill?.ano} · {c.questoes} questões · v{c.version}
                    </p>
                  </div>
                  <Badge variant="outline" className="w-fit">{STATUS_LABEL[c.status]}</Badge>
                  <div className="flex gap-1.5 flex-wrap">
                    <Button size="sm" variant="outline" data-testid={`estudio-ver-${c.id}`}
                            onClick={async () => {
                              const { data } = await api.get(`/dna-estudos/studio/contents/${c.id}`);
                              setDetail(data);
                            }}>Ver</Button>
                    {c.status === 'rascunho' && <Button size="sm" variant="outline" onClick={() => changeStatus(c.id, 'em_revisao')}>Enviar p/ revisão</Button>}
                    {c.status === 'em_revisao' && <Button size="sm" onClick={() => changeStatus(c.id, 'aprovado')}>Aprovar</Button>}
                    {c.status === 'aprovado' && <Button size="sm" data-testid={`estudio-publicar-${c.id}`} onClick={() => changeStatus(c.id, 'publicado')}>Publicar</Button>}
                    {c.status === 'publicado' && <Button size="sm" variant="outline" onClick={() => changeStatus(c.id, 'arquivado')}>Despublicar</Button>}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── PAINEL ─────────────────────────────── */}
        <TabsContent value="painel" className="space-y-3">
          {!painel ? <p className="text-sm text-muted-foreground">Carregando…</p> : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base md:text-lg flex items-center gap-2"><Users className="w-4 h-4" />Alunos</CardTitle></CardHeader>
                <CardContent className="space-y-1.5 max-h-[420px] overflow-y-auto">
                  {painel.alunos?.length === 0 && <p className="text-sm text-muted-foreground">Sem alunos no seu escopo.</p>}
                  {painel.alunos?.map(a => (
                    <div key={a.student_id} className="flex items-center justify-between border rounded-lg p-2 text-sm">
                      <div className="min-w-0">
                        <p className="font-medium truncate">{a.name}</p>
                        <p className="text-xs text-muted-foreground">{a.class_name || '—'} · {a.skills} habilidades · {a.minutes_total} min</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${a.mastery_avg >= 70 ? 'text-emerald-700' : a.mastery_avg >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                          {a.skills ? `${a.mastery_avg}%` : '—'}
                        </p>
                        {a.needs_intervention && <span className="text-[10px] text-red-600">precisa de apoio</span>}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base md:text-lg">Habilidades mais difíceis</CardTitle></CardHeader>
                <CardContent className="space-y-1.5 max-h-[420px] overflow-y-auto">
                  {painel.heatmap?.length === 0 && <p className="text-sm text-muted-foreground">Ainda sem dados de domínio.</p>}
                  {painel.heatmap?.map(h => (
                    <div key={h.skill_code} className="flex items-center gap-2 text-sm">
                      <span className="w-24 font-mono text-xs text-violet-800">{h.skill_code}</span>
                      <div className="flex-1 bg-slate-100 rounded h-2.5 overflow-hidden">
                        <div className={`h-full ${h.mastery_media >= 70 ? 'bg-emerald-500' : h.mastery_media >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                             style={{ width: `${h.mastery_media}%` }} />
                      </div>
                      <span className="w-16 text-right text-xs">{h.mastery_media}% ({h.alunos})</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* ── IA e CUSTOS ─────────────────────────────── */}
        <TabsContent value="ia" className="space-y-3">
          {settings && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base md:text-lg">Modelos de IA</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  ['content_provider', 'Provedor · conteúdo'], ['content_model', 'Modelo · conteúdo'],
                  ['tutor_provider', 'Provedor · tutor'], ['tutor_model', 'Modelo · tutor'],
                  ['tutor_daily_limit', 'Limite diário do tutor'], ['weekly_goal_minutes', 'Meta semanal (min)'],
                ].map(([key, label]) => (
                  <div key={key}>
                    <Label className="text-xs">{label}</Label>
                    <Input value={settings[key] ?? ''} data-testid={`estudos-setting-${key}`}
                           onChange={(e) => setSettings(s => ({ ...s, [key]: e.target.value }))} />
                  </div>
                ))}
                <div className="md:col-span-2">
                  <Button onClick={saveSettings} data-testid="estudos-salvar-settings">Salvar configurações</Button>
                  <p className="text-xs text-muted-foreground mt-2">
                    A chave da IA fica somente no backend. Provedores: openai · anthropic · gemini.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
          {usage && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base md:text-lg">Uso de IA (30 dias)</CardTitle>
                <p className="text-xs text-muted-foreground">Tokens estimados a partir do texto enviado/recebido.</p>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Kpi label="Chamadas" value={usage.total_calls} />
                  <Kpi label="Custo estimado" value={`US$ ${usage.total_cost_usd}`} tone="emerald" />
                  <Kpi label="Tokens entrada" value={usage.tokens_in} />
                  <Kpi label="Tokens saída" value={usage.tokens_out} />
                </div>
                {usage.by_purpose?.map(p => (
                  <div key={p.purpose} className="flex items-center justify-between text-sm border rounded-lg p-2">
                    <span className="font-medium">{p.purpose}</span>
                    <span className="text-muted-foreground text-xs">
                      {p.calls} chamadas · US$ {p.cost_usd} · {p.latency_avg_ms} ms · {p.errors} erros
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog de liberação */}
      <Dialog open={grantOpen} onOpenChange={setGrantOpen}>
        <DialogContent data-testid="estudos-grant-dialog">
          <DialogHeader><DialogTitle>Liberar DNA IA Estudos ({selected.length} aluno(s))</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Modalidade</Label>
              <Select value={grantForm.access_type} onValueChange={(v) => setGrantForm(f => ({ ...f, access_type: v }))}>
                <SelectTrigger data-testid="estudos-grant-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Início</Label>
                <Input type="date" value={grantForm.starts_at} data-testid="estudos-grant-inicio"
                       onChange={(e) => setGrantForm(f => ({ ...f, starts_at: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Validade</Label>
                <Input type="date" value={grantForm.ends_at} data-testid="estudos-grant-validade"
                       onChange={(e) => setGrantForm(f => ({ ...f, ends_at: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Motivo / observação</Label>
              <Input value={grantForm.reason} data-testid="estudos-grant-motivo"
                     onChange={(e) => setGrantForm(f => ({ ...f, reason: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantOpen(false)}>Cancelar</Button>
            <Button disabled={saving} data-testid="estudos-grant-confirmar"
                    onClick={() => runAction('grant', { student_ids: selected, ...grantForm }, 'Acesso liberado')}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Confirmar liberação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detalhe do conteúdo */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="estudio-detalhe">
          <DialogHeader><DialogTitle>{detail?.package?.titulo}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <p className="text-xs text-muted-foreground">
                {detail.skill_code} · {detail.skill?.componente} · {detail.skill?.ano} · status {STATUS_LABEL[detail.status]}
              </p>
              <p><strong>Objetivo:</strong> {detail.package?.objetivo}</p>
              {(detail.package?.explicacao || []).map((b, i) => (
                <div key={i} className="border-l-2 border-violet-200 pl-3">
                  <p className="font-medium">{b.titulo}</p>
                  <p className="text-slate-700">{b.texto}</p>
                </div>
              ))}
              <p><strong>Resumo:</strong> {detail.package?.resumo}</p>
              <div>
                <p className="font-medium">Questões ({(detail.package?.questoes || []).length})</p>
                {(detail.package?.questoes || []).map((q, i) => (
                  <div key={q.id || i} className="border rounded-lg p-2 my-1">
                    <p className="text-xs text-muted-foreground">{q.nivel}</p>
                    <p>{q.enunciado}</p>
                    <ul className="list-disc ml-5 text-slate-700">
                      {(q.alternativas || []).map((a, j) => (
                        <li key={j} className={a === q.correta ? 'text-emerald-700 font-medium' : ''}>{a}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
